import React, { useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { ContextMenu, ContextMenuBuilder, type RepeatRowsPayload } from '@/components/custom/erp/input-table-v2';
import { buildInvoiceDetailFile, hasInvoiceDetail } from '../../../utils/detail-export';
import type { DataTableRenderCellParams } from '@/components/custom/erp/data-table-v2';
import { ErpInputTableV2, useInputTableValidation } from '@/components/custom/erp';
import { constraintKey, constraintValues } from '@/lib/account-constraints';
import { useDetailExcelExport } from '@/hooks/use-detail-excel-export';
import { useAutoSelectBoPhan } from './hooks/useAutoSelectBoPhan';
import type { PurchaseInvoiceDetailRow } from '../../../types';
import { SelectedCellInfo, useDetailPaste } from '../hooks';
import type { AccountConstraint } from '@/types/schemas';
import { copyCellValue } from '@/lib/table-handlers';
import { getDetailTableColumns } from './columns';
import { QUERY_KEYS } from '@/constants';
import { FormMode } from '@/types/form';
import { useCRUD } from '@/hooks';

interface DetailTabProps {
  formMode: FormMode;
  rows: PurchaseInvoiceDetailRow[];
  selectedRowUuid?: string | null;
  selectedCell?: SelectedCellInfo | null;
  loai_ck?: number | string;
  onRowClick?: (params: MuaHangRecord) => void;
  onCellClick?: (params: DataTableRenderCellParams) => void;
  onAddRow: () => void;
  onDeleteRow: () => void;
  onCopyRow: () => void;
  onPasteRow: (clipboardText?: string) => void | Promise<void>;
  onMoveRow: (direction: 'up' | 'down') => void;
  onAllocateDiscount?: (mode: 'quantity' | 'value') => void;
  onCellValueChange: (rowUuid: string, field: string, newValue: MuaHangRecord) => void;
  onRepeatForRows: (payload: RepeatRowsPayload<MuaHangRecord>) => void;
  formState: MuaHangRecord;
  constraints?: AccountConstraint[];
  handleUpdateNonWritableFields?: (fields: Set<string>) => void;
  handleUpdatePasteConfigs?: (
    configs: Record<string, { resolver: (codes: string[]) => Promise<MuaHangRecord[]>; resultField?: string }>
  ) => void;
  isPasting?: boolean;
}

const detail = (row: Record<string, MuaHangRecord>) => ({
  ma_vt: row.ma_vt_data?.uuid || row.ma_vt || '',
  ma_kho: row.ma_kho_data?.uuid || row.ma_kho || '',
  so_luong: row.so_luong,
  ...constraintValues(row)
});

export const DetailTab: React.FC<DetailTabProps> = ({
  formState,
  formMode,
  rows,
  selectedRowUuid,
  selectedCell,
  loai_ck,
  onRowClick,
  onCellClick,
  onAddRow,
  onDeleteRow,
  onCopyRow,
  onPasteRow,
  onMoveRow,
  onAllocateDiscount,
  onCellValueChange,
  onRepeatForRows,
  constraints = [],
  handleUpdateNonWritableFields,
  handleUpdatePasteConfigs,
  isPasting
}) => {
  const [contextMenu, setContextMenu] = useState<{
    position: { x: number; y: number } | null;
    rowData: MuaHangRecord | null;
    columnField: string | null;
  }>({
    position: null,
    rowData: null,
    columnField: null
  });
  const { fetchDetail } = useCRUD({
    endpoint: QUERY_KEYS.VAT_TU,
    initialFetch: false
  });
  const xt_yn = useWatch({ name: 'xt_yn' });
  const handleCellValueChange = useAutoSelectBoPhan(onCellValueChange);
  const { cellError } = useInputTableValidation({
    rows,
    name: 'chi_tiet_hoa_don',
    map: detail,
    deps: constraintKey(constraints)
  });

  const columns = useMemo(
    () =>
      getDetailTableColumns(formMode, xt_yn, formState?.state?.ck_yn, handleCellValueChange, fetchDetail, cellError),
    [cellError, fetchDetail, formMode, formState?.state?.ck_yn, handleCellValueChange, xt_yn]
  );

  const exportOptions = useMemo(
    () => ({
      file: buildInvoiceDetailFile(columns),
      documentNumber: formState?.state?.soChungTu || undefined,
      includeRow: hasInvoiceDetail
    }),
    [columns, formState?.state?.soChungTu]
  );
  const detailExport = useDetailExcelExport(rows, exportOptions);

  useDetailPaste({
    columns,
    handleUpdateNonWritableFields,
    handleUpdatePasteConfigs
  });

  const contextMenuOptions = useMemo(
    () =>
      ContextMenuBuilder.buildOptions({
        contextMenu,
        columns,
        getRowId: row => row?.uuid || '',
        onCopyData: ({ columnValue, columnField, columnValues }) => {
          copyCellValue({
            columnField,
            columnValue,
            columnValues,
            fallback: text => {
              console.log('Copy data per column:', text);
            }
          });
        },
        onRepeatForRows,
        rowsCount: rows.length
      }),
    [columns, contextMenu, onRepeatForRows, rows.length]
  );

  return (
    <>
      <ErpInputTableV2
        data-perf-id='hoa-don-mua-hang-trong-nuoc-form-detail-table'
        rows={rows}
        autoHeight
        showIndexColumn
        bottomSelector='[data-arito-bottom-bar]'
        onRowClick={onRowClick as MuaHangRecord}
        onCellClick={onCellClick as MuaHangRecord}
        onCellContextMenu={(event, params) => {
          onCellClick?.(params as MuaHangRecord);
          const pointerEvent = event as MouseEvent;

          setContextMenu({
            position: { x: pointerEvent.clientX, y: pointerEvent.clientY },
            rowData: params.row,
            columnField: params.field
          });
        }}
        selectedRowId={selectedRowUuid || undefined}
        columns={columns as MuaHangRecord[]}
        getRowId={row => row?.uuid || ''}
        pasteTarget={selectedCell}
        actionLoading={isPasting || detailExport.isExporting}
        actionsDisabled={formMode === 'view'}
        actionOrder={[
          'add',
          'delete',
          'copy',
          'paste',
          'moveUp',
          'moveDown',
          'export',
          'pin',
          'viewInventory',
          'viewReceipt'
        ]}
        actions={{
          add: onAddRow,
          delete: onDeleteRow,
          copy: onCopyRow,
          paste: onPasteRow,
          moveUp: () => onMoveRow('up'),
          moveDown: () => onMoveRow('down'),
          export: detailExport.exportToExcel,
          pin: () => console.log('Pin clicked'),
          viewInventory: () => console.log('View inventory clicked'),
          viewReceipt: () => console.log('View receipt clicked')
        }}
        actionMenu={
          Number(loai_ck ?? formState?.state?.loai_ck) === 3
            ? {
                label: 'Phân bổ chiết khấu',
                icon: 139,
                items: [
                  { key: 'quantity', label: 'Theo số lượng', onSelect: () => onAllocateDiscount?.('quantity') },
                  { key: 'value', label: 'Theo giá trị', onSelect: () => onAllocateDiscount?.('value') }
                ]
              }
            : undefined
        }
      />

      <ContextMenu
        options={contextMenuOptions}
        position={contextMenu.position}
        onClose={() =>
          setContextMenu({
            position: null,
            rowData: null,
            columnField: null
          })
        }
      />
    </>
  );
};
